<template>
  <!-- NAV HEADER -->
  <nav
    class="bleach-nav bleach-nav--inset"
    :style="{
      transform:
        scrollLockOffset > 0 ? `translateY(${scrollLockOffset}px)` : null
    }"
    tabindex="-1"
  >
    <div class="bleach-nav__header">
      <div class="bleach-nav__header-background-holder">
        <slot name="background">
          <div
            class="bleach-nav__header-background"
            :style="{
              backgroundColor: getNavColour,
              opacity: getDynamicOpacity
            }"
            :class="{
              'bleach-nav__header-background--transparent':
                GET_IS_BELOW_BREAKPOINT('md') && navIsTransparent
            }"
          ></div>
        </slot>
      </div>
      <div class="bleach-nav__header-content flex--row-center-center">
        <div class="bleach-nav__header-items flex--row-start-center">
          <!-- Nav trigger -->
          <template v-if="GET_IS_BELOW_BREAKPOINT('lg')">
            <div class="bleach-nav__item-holder">
              <MenuControl
                :active="navActiveElement === 'menu'"
                :uiColor="getUiColour"
              />
            </div>
            <div
              v-if="GET_IS_BELOW_BREAKPOINT('md')"
              class="bleach-nav__item-holder"
            >
              <AccountControl
                :active="navActiveElement === 'login'"
                :uiColor="getUiColour"
              />
            </div>
          </template>
          <div
            v-if="GET_IS_ABOVE_BREAKPOINT('lg')"
            class="bleach-nav__item-holder"
          >
            <StoreControl
              class="bleach-nav__store-control"
              :uiColor="getUiColour"
            />
          </div>
        </div>
        <div
          class="bleach-nav__header-items bleach-nav__header-items--logo flex--col-center-center"
        >
          <LinkTag class="bleach-nav__logo-link" :link="`/`">
            <template>
              <div class="bleach-nav__logo-sizer flex--col-center-center">
                <BleachLogo
                  class="bleach-nav__logo"
                  :style="{ fill: getUiColour }"
                />
              </div>
            </template>
          </LinkTag>
        </div>
        <div class="bleach-nav__header-items flex--row-end-center">
          <div
            v-if="GET_IS_ABOVE_BREAKPOINT('md')"
            class="bleach-nav__item-holder bleach-nav__item-holder--end"
          >
            <AccountControl
              :active="navActiveElement === 'login'"
              :uiColor="getUiColour"
            />
          </div>
          <!-- Search trigger -->
          <div class="bleach-nav__item-holder bleach-nav__item-holder--end">
            <SearchControl
              :active="navActiveElement === 'search'"
              :uiColor="getUiColour"
            />
          </div>
          <!-- Cart -->
          <div class="bleach-nav__item-holder bleach-nav__item-holder--end">
            <CartControl :uiColor="getUiColour" />
          </div>
        </div>
      </div>
    </div>
    <!-- MENU LAYER -->
    <div class="bleach-nav__menu">
      <NavMenu :uiColor="getUiColour" />
    </div>
  </nav>
</template>

<script>
import MenuControl from "./../../components/Navigation/MenuControl.vue"
import StoreControl from "./../../components/Navigation/StoreControl.vue"
import SearchControl from "./../../components/Navigation/SearchControl.vue"
import CartControl from "./../../components/Navigation/CartControl.vue"
import AccountControl from "./../../components/Navigation/AccountControl.vue"
import BleachLogo from "./../../assets/bleach_logo.svg"

import NavMenu from "./../NavigationMenu/NavigationMenu.vue"

import styles from "@/styles/settings.module.scss"

// import GTMAPI from "@/integration/GTMAPI"

import { mapState, mapActions, mapMutations, mapGetters } from "vuex"

import "./NavigationStyles.scss"

export default {
  props: {
    backgroundColor: String,
    dynamicOpacityThreshold: Number,
    uiColor: String
  },

  data: () => ({
    styles
  }),

  components: {
    MenuControl,
    StoreControl,
    AccountControl,
    CartControl,
    SearchControl,
    BleachLogo,
    NavMenu
  },

  created() {},

  methods: {
    ...mapActions("cart", ["OPEN_CART", "CLOSE_CART"]),
    ...mapMutations("ui", ["SET_NAV_ACTIVE_ELEMENT"])
  },

  computed: {
    ...mapState("ui", [
      "navBaseColour",
      "navUiColour",
      "navIsTransparent",
      "navActiveElement"
    ]),
    ...mapState("scroll", ["scrollLockOffset", "scrollPos"]),
    ...mapGetters("breakpoints", [
      "GET_IS_ABOVE_BREAKPOINT",
      "GET_IS_BELOW_BREAKPOINT"
    ]),

    getDynamicOpacity() {
      if (!this.dynamicOpacityThreshold || this.GET_IS_ABOVE_BREAKPOINT("md")) {
        return 1
      } else {
        return Math.min(this.scrollPos / this.dynamicOpacityThreshold, 1)
      }
    },

    getNavColour() {
      const defaultColour = this.styles["COLOUR_bleach_offwhite"]
      const targetColour = this.backgroundColor || this.navBaseColour
      if (
        this.GET_IS_ABOVE_BREAKPOINT("md") &&
        !!targetColour &&
        this.scrollPos <= this.dynamicOpacityThreshold
      ) {
        // calculate an interpolation between the default offwhite and the targetColour (along range 0 to 1)
        const interpolationPos = Math.min(
          this.scrollPos / this.dynamicOpacityThreshold,
          1
        )
        return `color-mix(in oklch, ${defaultColour} ${100 -
          100 * interpolationPos}%, ${targetColour} ${100 *
          interpolationPos}% )`
      } else {
        return targetColour || defaultColour
      }
    },

    getUiColour() {
      const defaultColour = styles["COLOUR_bleach_black"]
      const targetColour = this.uiColor || this.navUiColour
      if (
        this.GET_IS_ABOVE_BREAKPOINT("md") &&
        !!targetColour &&
        this.scrollPos <= this.dynamicOpacityThreshold
      ) {
        // calculate an interpolation between the default offwhite and the targetColour (along range 0 to 1)
        const interpolationPos = Math.min(
          this.scrollPos / this.dynamicOpacityThreshold,
          1
        )
        return `color-mix(in oklch, ${defaultColour} ${100 -
          100 * interpolationPos}%, ${targetColour} ${100 *
          interpolationPos}% )`
      } else {
        return targetColour || defaultColour
      }
    },

    navActive() {
      return ["search", "menu"].includes(this.navActiveElement)
    }
  }
}
</script>

<style lang="scss"></style>
